import { computed, onScopeDispose, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { AppLocale } from '@/i18n/messages'
import { normalizePipSkin, normalizePipStreamOptions, type PipSkin } from '@/ui/pip-skins'
import { normalizePerformanceSettings } from '@/core/performance-settings'

export type ThemePreference = 'light' | 'dark'

function initialTheme(): ThemePreference {
  const stored = localStorage.getItem('maple-log:theme')
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function initialStreamOptions() {
  try { return normalizePipStreamOptions(JSON.parse(localStorage.getItem('maple-log:pip-stream') ?? 'null')) }
  catch { return normalizePipStreamOptions(null) }
}

function initialPerformanceSettings() {
  try { return normalizePerformanceSettings(JSON.parse(localStorage.getItem('maple-log:performance') ?? 'null')) }
  catch { return normalizePerformanceSettings(null) }
}

export const useAppStore = defineStore('app', () => {
  const locale = ref<AppLocale>(localStorage.getItem('maple-log:locale') === 'en' ? 'en' : 'zh-CN')
  const theme = ref<ThemePreference>(initialTheme())
  const pipSkin = ref<PipSkin>(normalizePipSkin(localStorage.getItem('maple-log:pip-skin')))
  const pipStream = ref(initialStreamOptions())
  const performanceSettings = ref(initialPerformanceSettings())
  const presetId = ref(localStorage.getItem('maple-log:preset') ?? 'mxdc')
  const resolvedTheme = computed(() => theme.value)

  watch(locale, (value) => localStorage.setItem('maple-log:locale', value))
  watch(theme, (value) => localStorage.setItem('maple-log:theme', value))
  watch(pipSkin, (value) => localStorage.setItem('maple-log:pip-skin', value))
  watch(pipStream, (value) => localStorage.setItem('maple-log:pip-stream', JSON.stringify(value)), { deep: true })
  watch(performanceSettings, (value) => {
    try { localStorage.setItem('maple-log:performance', JSON.stringify(normalizePerformanceSettings(value))) } catch { /* memory-only when storage is unavailable */ }
  }, { deep: true })
  watch(presetId, (value) => localStorage.setItem('maple-log:preset', value))
  watch(resolvedTheme, (value) => {
    document.documentElement.dataset.theme = value
    document.documentElement.style.colorScheme = value
  }, { immediate: true })

  // Keep a recording tab's overlay in sync with settings changed in another tab.
  const syncWidgetSettings = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return
    if (event.key === 'maple-log:pip-skin') pipSkin.value = normalizePipSkin(event.newValue)
    if (event.key === 'maple-log:pip-stream') pipStream.value = initialStreamOptions()
    if (event.key === 'maple-log:performance') performanceSettings.value = initialPerformanceSettings()
    if (event.key === 'maple-log:locale') locale.value = event.newValue === 'en' ? 'en' : 'zh-CN'
    if (event.key === 'maple-log:theme' && (event.newValue === 'light' || event.newValue === 'dark')) theme.value = event.newValue
  }
  window.addEventListener('storage', syncWidgetSettings)
  onScopeDispose(() => window.removeEventListener('storage', syncWidgetSettings))

  function toggleTheme(): void {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  return { locale, theme, pipSkin, pipStream, performanceSettings, presetId, resolvedTheme, toggleTheme }
})
